// The venue's photos (spec/13 §13 "photos (cover and gallery)"; #403 rule 3) — the one control the
// renderer cannot build from a schema, because a value here is made by uploading a file. It is a
// custom control (`x-clavian-control: photos`) and follows the same contract as every other: it
// shows a value, and hands a new one to `onChange`; the venue's Save attaches it.

import { useProblem } from '../api/use-problem';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Field } from '@clavian/ui-web';
import { createContext, useContext, useEffect, useRef, useState, type ChangeEvent } from 'react';

import type { ControlProps } from '../settings/types';
import './photos.css';
import { PHOTO_TYPES, PhotoUploadError, type Photo } from './uploads';

export interface PhotoUploader {
  readonly upload: (file: File) => Promise<Photo>;
  readonly whenReady: (fileId: string, signal: AbortSignal) => Promise<Photo | undefined>;
  /** The venue's logo (`venue_logo`, `LogoControl`); absent, the logo is read only. */
  readonly uploadLogo?: (file: File) => Promise<Photo>;
}

export const UploaderContext = createContext<PhotoUploader | null>(null);
export const PhotoUploaderProvider = UploaderContext.Provider;

/** The contract allows 20 gallery photos (`VenuePhotos.gallery.maxItems`). */
const GALLERY_LIMIT = 20;

/** The venue's `photos`, as the screen shows them. */
interface PhotoSet {
  readonly cover: Photo | null;
  readonly gallery: readonly Photo[];
}

export function PhotosControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const uploader = useContext(UploaderContext);
  const photos = photoSet(props.value);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Sizes the worker made after the upload, shown before the next save brings them back on the venue.
  const [ready, setReady] = useState<Readonly<Record<string, Photo>>>({});
  const aborts = useRef<AbortController[]>([]);
  useEffect(() => () => aborts.current.forEach((controller) => controller.abort()), []);
  // An upload takes seconds, and the photos may change meanwhile (a removal, Discard, a 412's fresh
  // version): what it adds to is the value as it is when it finishes, not as it was when it began.
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });

  const disabled = props.disabled || uploader === null;
  const shown = (photo: Photo): Photo => ready[photo.fileId] ?? photo;

  const add = async (event: ChangeEvent<HTMLInputElement>, into: 'cover' | 'gallery') => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined || uploader === null) return;
    setBusy(true);
    setStatus(t('venueProfile.photos.uploading', { name: file.name }));
    try {
      const photo = await uploader.upload(file);
      const now = photoSet(latest.current.value);
      latest.current.onChange(into === 'cover' ? { ...now, cover: photo } : { ...now, gallery: [...now.gallery, photo] });
      setStatus(t(photo.variants.length > 0 ? 'venueProfile.photos.uploaded' : 'venueProfile.photos.processing'));
      if (photo.variants.length === 0) {
        const controller = new AbortController();
        aborts.current.push(controller);
        void uploader.whenReady(photo.fileId, controller.signal).then((done) => {
          if (done === undefined || controller.signal.aborted) return;
          setReady((current) => ({ ...current, [done.fileId]: done }));
          setStatus(t('venueProfile.photos.uploaded'));
        });
      }
    } catch (error) {
      const reason = error instanceof PhotoUploadError ? error.reason : 'failed';
      setStatus(error instanceof PhotoUploadError ? t(`venueProfile.photos.${reason}`) : problem.text(error, { fallback: t(`venueProfile.photos.${reason}`) }));
    } finally {
      setBusy(false);
    }
  };

  const galleryFull = photos.gallery.length >= GALLERY_LIMIT;

  return (
    <Field as="group" label={props.label} description={t('venueProfile.photos.hint')} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-photos">
          <fieldset className="console-photos__set">
            <legend className="clav-label">{t('venueProfile.photos.cover')}</legend>
            {photos.cover === null ? (
              <p className="console-muted">{t('venueProfile.photos.noCover')}</p>
            ) : (
              <figure className="console-photo console-photo--cover">
                <Picture photo={shown(photos.cover)} alt={t('venueProfile.photos.coverAlt')} sizes="(min-width: 60rem) 32rem, 100vw" />
              </figure>
            )}
            {disabled ? null : (
              <div className="console-photos__actions">
                <FileButton
                  label={photos.cover === null ? t('venueProfile.photos.uploadCover') : t('venueProfile.photos.replaceCover')}
                  busy={busy}
                  onFile={(event) => void add(event, 'cover')}
                />
                {photos.cover === null ? null : (
                  <Button size="sm" variant="ghost" onClick={() => props.onChange({ ...photos, cover: null })}>
                    {t('venueProfile.photos.remove', { photo: t('venueProfile.photos.coverAlt') })}
                  </Button>
                )}
              </div>
            )}
          </fieldset>

          <fieldset className="console-photos__set">
            <legend className="clav-label">{t('venueProfile.photos.gallery')}</legend>
            {photos.gallery.length === 0 ? <p className="console-muted">{t('venueProfile.photos.noGallery')}</p> : null}
            <ul className="console-photos__grid">
              {photos.gallery.map((photo, index) => {
                const alt = t('venueProfile.photos.galleryAlt', { n: index + 1 });
                return (
                  <li key={photo.fileId} className="console-photo">
                    <Picture photo={shown(photo)} alt={alt} sizes="12rem" />
                    {disabled ? null : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => props.onChange({ ...photos, gallery: photos.gallery.filter((_, at) => at !== index) })}
                      >
                        {t('venueProfile.photos.remove', { photo: alt })}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
            {disabled ? null : galleryFull ? (
              <p className="console-muted">{t('venueProfile.photos.galleryFull')}</p>
            ) : (
              <div className="console-photos__actions">
                <FileButton label={t('venueProfile.photos.addToGallery')} busy={busy} onFile={(event) => void add(event, 'gallery')} />
              </div>
            )}
          </fieldset>

          <p className="console-muted" role="status">
            {status}
          </p>
        </div>
      )}
    </Field>
  );
}

function Picture({ photo, alt, sizes }: { readonly photo: Photo; readonly alt: string; readonly sizes: string }) {
  const srcSet = photo.variants.map((variant) => `${variant.url} ${variant.width}w`).join(', ');
  return <img src={photo.url} alt={alt} loading="lazy" decoding="async" {...(srcSet ? { srcSet, sizes } : {})} />;
}

/** A file input dressed as the kit's button; the input itself is the control, so it is named and focusable. */
export function FileButton({
  label,
  busy,
  onFile,
  accept = PHOTO_TYPES,
}: {
  readonly label: string;
  readonly busy: boolean;
  readonly onFile: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly accept?: readonly string[];
}) {
  return (
    <label className="clav-button clav-button--secondary clav-button--sm console-file" aria-busy={busy || undefined}>
      <input type="file" accept={accept.join(',')} className="console-file__input" disabled={busy} onChange={onFile} />
      {label}
    </label>
  );
}

function isPhoto(value: unknown): value is Photo {
  return typeof value === 'object' && value !== null && typeof (value as Photo).fileId === 'string';
}

function photoSet(value: unknown): PhotoSet {
  if (typeof value !== 'object' || value === null) return { cover: null, gallery: [] };
  const { cover, gallery } = value as { cover?: unknown; gallery?: unknown };
  return {
    cover: isPhoto(cover) ? cover : null,
    gallery: Array.isArray(gallery) ? gallery.filter(isPhoto) : [],
  };
}
